import { useEffect, useState } from 'react';
import { NEWS_FALLBACK_IMAGES, isYahooPlaceholder, pickFallbackImage } from '@/lib/newsFallbackImages';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Picture slot for a news article. Order: article image → company
// square icon → banner logo → random stock-market artwork (lib/newsFallbackImages).
// Oct 2026: Finnhub's generic "yahoo!finance" brand image is NOT treated as a
// photo — it steps straight to the company icon/logo, and if we have neither,
// the random pool includes that Yahoo image alongside our artwork.
export default function NewsArticleImage({ article, branding, className = '', variant = 'tile' }) {
  const abs = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);
  const yahooPlaceholder = isYahooPlaceholder(article?.image_url);
  const sources = [
    article?.image_url && !yahooPlaceholder ? { src: article.image_url, kind: 'photo' } : null,
    branding?.icon_url ? { src: abs(branding.icon_url), kind: 'icon' } : null,
    branding?.logo_url ? { src: abs(branding.logo_url), kind: 'logo' } : null,
  ].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const [photo, setPhoto] = useState(null);
  useEffect(() => { setIdx(0); setPhoto(null); }, [article?.url, article?.image_url, branding?.logo_url, branding?.icon_url]);
  const cur = sources[idx];
  const isTile = variant === 'tile';

  if (!cur) {
    // Final fallback: a deterministic-random pick from our stock-market artwork
    // (same article → same picture). Yahoo-placeholder articles add that image
    // to the pool so it still shows up sometimes, just not every time.
    const pool = yahooPlaceholder ? [...NEWS_FALLBACK_IMAGES, article.image_url] : NEWS_FALLBACK_IMAGES;
    const src = pickFallbackImage(article?.url || article?.title || article?.ticker, pool);
    const isArtwork = src !== article?.image_url;
    return (
      <div className={`${isArtwork ? 'bg-muted' : 'bg-white'} overflow-hidden ${className}`} data-testid={`news-image-placeholder-${article?.ticker}`}>
        <img
          src={src}
          alt=""
          className={`w-full h-full ${isArtwork ? 'object-cover' : 'object-contain p-3'} ${isTile ? 'group-hover:scale-105 transition-transform' : ''}`}
          loading="lazy"
        />
      </div>
    );
  }

  if (cur.kind === 'photo') {
    // Publisher "photos" are often wide wordmark logos; object-cover would
    // crop them to a few letters, so letterbox logo-shaped (very wide/tall
    // or tiny) images on a mid-tone plate instead.
    //
    // Sep 26 2026 — threshold tightened. The old `r > 2.4` cutoff was
    // built for banner logos, but the most common wordmark case in the
    // wild is the Open Graph share image (Facebook rec 1200×630 =
    // ~1.91:1) which Benzinga, GlobeNewswire, PR Newswire and many
    // publishers use as the article thumbnail — those slipped through
    // and got cropped to the middle few letters ("b! fir", "AYFIR").
    // 1.85 still leaves standard 16:9 photos (1.78) and 3:2 photos
    // (1.5) on `cover`, so real journalistic images keep filling the
    // tile. Also raised the tiny-image cutoff from 300 → 400 so the
    // small RSS thumbnails publishers ship for logo rows land on
    // `contain` rather than being stretched.
    const letterbox = photo != null && (photo.r > 1.85 || photo.r < 0.55 || photo.max < 400);
    // Sep 27 2026 — the previous approach used a scale-150 + blur-2xl
    // copy of the same image as the backdrop, hoping the blur would
    // dissolve it into a soft halo. In practice high-res wordmark
    // images survived the blur as giant ghost letters behind the
    // real image. We now pick a SOLID contrasting plate: sample the
    // wordmark's average brightness on load and use a dark plate for
    // light-dominant images (BayFirst on white) or a light plate for
    // dark-dominant images (Yahoo purple), so the wordmark text
    // always has contrast around it.
    const plateBg = photo?.dark == null
      ? 'bg-muted'
      : photo.dark
        ? 'bg-zinc-100 dark:bg-zinc-200'   // dark image → light plate
        : 'bg-zinc-800 dark:bg-zinc-900';  // light image → dark plate
    // Sep 27 2026 — cached-image onLoad workaround. Chrome (and some
    // other browsers) skip firing `onLoad` for images that were
    // already fully cached BEFORE the <img> element was created —
    // e.g., when React Router re-mounts the /news page after a
    // navigation, the memory cache serves the bytes instantly and
    // `onLoad` never fires. Result: `photo` stays null, `letterbox`
    // stays false, and object-cover crops the wordmark until the
    // user hard-refreshes. Fix: attach a ref callback that inspects
    // the element on mount; if the image is already complete, run
    // the same read-and-sample logic manually. Extracted into a
    // named handler so onLoad and the ref callback share it.
    const readAndSample = (img) => {
      if (!img) return;
      const { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) return;
      let dark = null;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 16; canvas.height = 16;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 16, 16);
        const data = ctx.getImageData(0, 0, 16, 16).data;
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
          sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        }
        const avg = sum / (data.length / 4);
        dark = avg < 128;
      } catch {
        // CORS-tainted canvas — leave plate neutral.
      }
      setPhoto({ r: w / h, max: Math.max(w, h), dark });
    };
    return (
      <div className={`relative overflow-hidden ${letterbox ? plateBg : 'bg-muted'} ${className}`}>
        <img
          src={cur.src}
          alt=""
          crossOrigin="anonymous"
          ref={(el) => {
            // If the browser served this image from cache before the
            // element was created, `complete` is true and onLoad
            // won't fire. Run the sample manually in that case.
            if (el && el.complete && el.naturalWidth > 0 && photo == null) {
              readAndSample(el);
            }
          }}
          className={`relative w-full h-full ${letterbox ? 'object-contain p-3 drop-shadow-md' : 'object-cover'} ${isTile ? 'group-hover:scale-105 transition-transform' : ''}`}
          onLoad={(e) => readAndSample(e.currentTarget)}
          onError={() => setIdx((i) => i + 1)}
          data-testid={`news-image-photo-${article?.ticker}`}
        />
      </div>
    );
  }

  // Company branding. Icons sit on the soft card plate; banner logos get
  // a mid-tone plate so both white and black marks stay legible.
  // Oct 2026 — dark-mode plate lightened (zinc-500/600 → zinc-400/500):
  // PBF's red/blue and Okeanis' charcoal wordmarks were vanishing on the
  // darker grey; white marks keep a drop shadow for separation.
  const plate = cur.kind === 'logo'
    ? 'bg-gradient-to-br from-zinc-300 to-zinc-400 dark:from-zinc-400 dark:to-zinc-500'
    : 'bg-gradient-to-br from-muted to-muted/40 dark:from-zinc-800 dark:to-zinc-900';
  return (
    <div
      className={`${plate} flex items-center justify-center overflow-hidden ${className}`}
      title={`${article?.ticker} company ${cur.kind === 'logo' ? 'logo' : 'icon'}`}
      data-testid={`news-image-${cur.kind}-${article?.ticker}`}
    >
      <img
        src={cur.src}
        alt={`${article?.ticker} logo`}
        className={cur.kind === 'logo'
          ? (isTile ? 'max-w-[80%] max-h-[70%] object-contain drop-shadow-md' : 'max-w-[92%] max-h-[85%] object-contain drop-shadow-md')
          : (isTile ? 'h-full w-auto max-w-full object-contain' : 'w-full h-full object-cover')}
        onError={() => setIdx((i) => i + 1)}
      />
    </div>
  );
}
